import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ImageCropModal from '../imageCrop/ImageCropModal';
import translate from '../translate';
import { resolveVideoUrl } from '../../services/utilityFunctions';

function ImageInput(props) {
  const {
    t,
    id,
    inputId,
    name,
    type,
    groupClassName,
    labelClassName,
    containerClassName,
    inputClassName,
    label,
    initialValue,
    placeholder,
    onChange,
    disabled,
    inputProps,
    handleCrop,
  } = props;

  // BRAVON-6332 Phase 2b — `User.video` is stored as the authenticated
  // `/file/user-video/<id>` route once its writer flips to the private bucket,
  // and neither `<video src>` nor `<img src>` can send a Bearer token. Re-based
  // onto this portal's own session-gated `/file/*`; anything else (a legacy
  // public url, the placeholder path, a `data:` url set after a pick) is
  // returned untouched.
  const [url, setUrl] = useState(resolveVideoUrl(initialValue) || placeholder);
  const [showActions, setShowActions] = useState(!!initialValue);
  const [showCropTool, setShowCropTool] = useState(false);
  const [{ originalFile, fileName, fileType }, setFile] = useState({
    originalFile: null,
    fileName: '',
    fileType: '',
  });

  const canChange = !disabled && onChange;
  const isImage = type === 'image';
  const isVideo = type === 'video';

  const resetState = () => {
    const input = document.querySelector(`#${inputId}`);
    if (input) {
      input.value = null;
    }
    setShowCropTool(false);
    setFile({ originalFile: null, fileName: '' });
  };

  const readAsDataUrl = blob =>
    new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = readerEvt => resolve(readerEvt.target.result);
      reader.readAsDataURL(blob);
    });

  const handleModalClose = blob => {
    if (blob) {
      onChange({ blob, fileName });
      readAsDataUrl(blob).then(dataUrl => {
        resetState();
        setShowActions(true);
        setUrl(dataUrl);
      });
    } else {
      resetState();
    }
  };

  const clearImage = () => {
    setUrl(placeholder);
    setShowActions(false);
    resetState();
    onChange(null);
  };

  const handleChange = evt => {
    const file = evt.currentTarget.files[0];
    if (isImage && handleCrop) {
      readAsDataUrl(file).then(dataUrl => {
        setFile({ originalFile: dataUrl, fileName: file.name, fileType: file.type });
        setShowCropTool(true);
      });
    } else {
      setFile({ originalFile: file, fileName: file.name, fileType: file.type });
    }
  };

  const handleImageClick = () => {
    const input = document.querySelector(`#${inputId}`);
    if (input) {
      input.click();
    }
  };

  useEffect(
    () => {
      if (isVideo || !handleCrop) {
        handleModalClose(originalFile);
      }
    },
    [originalFile],
  );

  return (
    <div className={groupClassName}>
      <label htmlFor={inputId} className={labelClassName}>
        {label}
        {canChange && (
          <input
            type="file"
            id={inputId}
            style={{ display: 'none' }}
            onChange={handleChange}
            disabled={disabled}
          />
        )}
      </label>
      <div className={`image-input-container ${containerClassName}`}>
        {(isImage || !showActions) && (
          <img
            style={canChange ? { cursor: 'pointer' } : undefined}
            onClick={handleImageClick}
            id={id}
            name={name}
            className={inputClassName}
            src={url}
            alt="upload placeholder"
          />
        )}
        {isVideo &&
          showActions && (
            <video
              style={canChange ? { cursor: 'pointer' } : undefined}
              onClick={handleImageClick}
              id={id}
              className={inputClassName}
              src={url}
            />
          )}
        {showActions && !disabled && (
          <div className="image-input-actions">
            <button
              onClick={clearImage}
              type="button"
              className="btn m-btn--square btn-danger"
            >
              {t('remove')}
            </button>
            <button
              onClick={handleImageClick}
              type="button"
              className="btn m-btn--square btn-secondary"
            >
              {t('change')}
            </button>
          </div>
        )}
      </div>
      {showCropTool && (
        <ImageCropModal
          title={t('image_crop')}
          originalFile={originalFile}
          fileType={fileType}
          onFinished={handleModalClose}
          {...inputProps}
        />
      )}
    </div>
  );
}

ImageInput.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.string,
  inputId: PropTypes.string,
  name: PropTypes.string,
  type: PropTypes.oneOf(['image', 'video']),
  label: PropTypes.string,
  initialValue: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  groupClassName: PropTypes.string,
  labelClassName: PropTypes.string,
  containerClassName: PropTypes.string,
  inputClassName: PropTypes.string,
  disabled: PropTypes.bool,
  inputProps: PropTypes.shape({
    cropWidth: PropTypes.number,
    cropHeight: PropTypes.number,
    cropAspect: PropTypes.number,
    cropLocked: PropTypes.bool,
  }),
  handleCrop: PropTypes.bool,
};

ImageInput.defaultProps = {
  id: 'image',
  inputId: 'fileInputUpload',
  name: 'image',
  type: 'image',
  label: '',
  initialValue: null,
  placeholder: '/images/no_image_upload.png',
  onChange: null,
  groupClassName: 'form-group m-form__group row',
  labelClassName: 'col-2',
  containerClassName: 'col-7',
  inputClassName: 'image-input-preview',
  disabled: false,
  inputProps: {},
  handleCrop: true,
};

export default translate(ImageInput);
